import React, { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  CircularProgress,
  Tab,
  Tabs,
} from '@mui/material'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useNavigate } from 'react-router-dom'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { AxiosError } from 'axios'
import SoapTemplateSection from '@/apps/legacy/components/portal/SoapTemplateSection'
import DrugTable from '@/apps/legacy/components/portal/DrugTable'
import {
  useCreateDrugGroupMutation,
  useAddDrugsToGroupMutation,
  useDrugSearchQuery,
  useDrugGroupDrugs,
} from '@/features/internal/drugs'
import { useUpdateGroupSoapTemplatesMutation } from '@/features/internal/drugs'
import {
  SoapTemplateGroup,
  SoapType,
} from '@/features/internal/drugs/types/soapTemplate'
import {
  ActionButton,
  CancelButton,
  SelectionCount,
} from '@/apps/legacy/components/portal/CustomButton'
import { useToast } from '@/shared/contexts/ToastContext'

/**
 * 医薬品グループ新規作成画面
 */
const DrugGroupCreatePage: React.FC = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()

  // グループ作成 Mutation（TanStack Query）
  const createGroupMutation = useCreateDrugGroupMutation()
  const updateSoapTemplatesMutation = useUpdateGroupSoapTemplatesMutation()
  const addDrugsToGroupMutation = useAddDrugsToGroupMutation()

  // 新規作成時は 'create' を渡す（useGroupDrugsQuery は enabled: false になる）
  const {
    loading: _drugLoading,
    error: drugError,
    selectedDrugIds,
    selectAllDrugs,
    deselectAllDrugs,
    setSelectedDrugIds,
  } = useDrugGroupDrugs('create')

  // 医薬品検索（TanStack Query）
  const {
    normalSearchKeyword,
    setNormalSearchKeyword,
    executeNormalSearch,
    searchResults,
    isLoading: drugsLoading,
    currentPage,
  } = useDrugSearchQuery()

  // タブの状態管理
  const [activeTab, setActiveTab] = useState(0)

  // 状態管理
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')
  const [nameError, setNameError] = useState('')
  const [soapTemplates, setSoapTemplates] = useState<SoapTemplateGroup[]>([
    {
      id: 'new-template-group',
      name: '基本テンプレート',
      keywords: [],
      templates: [
        {
          id: `new-item-${Date.now()}`,
          soap_type: 'S' as SoapType,
          content: '',
        },
      ],
    },
  ])
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [partialSuccess, setPartialSuccess] = useState(false)
  const [emptyItemsWarning, setEmptyItemsWarning] = useState<{
    show: boolean
    items: { groupName: string; soapType: string }[]
  }>({
    show: false,
    items: [],
  })

  // エラー情報の詳細化
  const [partialErrors, setPartialErrors] = useState<
    {
      type: 'soap' | 'drugs'
      message: string
    }[]
  >([])
  const [errorMessage, setErrorMessage] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [createdGroupId, setCreatedGroupId] = useState<string>('')

  // 検索結果データの導出
  const drugs = searchResults?.results ?? []
  const totalPages = searchResults
    ? Math.ceil(searchResults.count / (searchResults.page_size || 10))
    : 1

  // ページ変更時のハンドラー
  const handlePageChange = (newPage: number) => {
    executeNormalSearch(normalSearchKeyword, newPage)
  }

  // 検索実行ハンドラー
  const handleSearch = () => {
    executeNormalSearch(normalSearchKeyword, 1)
  }

  // フォーム検証
  const validateForm = () => {
    let isValid = true

    if (!groupName.trim()) {
      setNameError('グループ名は必須です')
      isValid = false
    } else {
      setNameError('')
    }

    return isValid
  }

  // 空のSOAP要素をチェックする関数
  const validateEmptyItems = () => {
    const emptyItems: { groupName: string; soapType: string }[] = []

    // 全てのテンプレートグループをチェック
    soapTemplates.forEach((group) => {
      // 各グループ内のSOAP項目をチェック
      group.templates.forEach((item) => {
        // contentが空の場合は問題ありとマーク
        if (!item.content.trim()) {
          emptyItems.push({
            groupName: group.name,
            soapType: item.soap_type,
          })
        }
      })
    })

    return emptyItems
  }

  // グループ保存と薬剤追加のフロー
  const handleSave = async () => {
    // 1. エラー状態のリセット
    clearAllErrors()
    setSaveSuccess(false)
    setPartialSuccess(false)
    setPartialErrors([])
    setCreatedGroupId('')

    // 2. 入力検証
    const isValid = validateForm()
    if (!isValid) return

    // 空のSOAP要素をチェック
    const emptyItems = validateEmptyItems()

    // 空の要素がある場合は警告を表示して処理中断
    if (emptyItems.length > 0) {
      setEmptyItemsWarning({
        show: true,
        items: emptyItems,
      })
      return
    }

    // 警告表示をクリア
    setEmptyItemsWarning({
      show: false,
      items: [],
    })

    // 3. 処理状態の管理
    setIsProcessing(true)

    try {
      // グループ作成（Mutation使用）
      const newGroup = await createGroupMutation.mutateAsync({
        name: groupName,
        description: groupDescription,
      })

      if (newGroup?.id) {
        // 作成されたグループIDを保存
        setCreatedGroupId(newGroup.id)

        const errors: { type: 'soap' | 'drugs'; message: string }[] = []

        // SOAPテンプレート更新（Mutation使用）
        try {
          await updateSoapTemplatesMutation.mutateAsync({
            groupId: newGroup.id,
            templateGroups: soapTemplates,
          })
        } catch (soapErr) {
          const axiosError = soapErr as AxiosError
          errors.push({
            type: 'soap' as const,
            message:
              (axiosError.response?.data as { detail?: string })?.detail ||
              'SOAPテンプレートの保存に失敗しました',
          })
        }

        // 選択された医薬品がある場合は追加（Mutation使用）
        if (selectedDrugIds.length > 0) {
          try {
            await addDrugsToGroupMutation.mutateAsync({
              groupId: newGroup.id,
              drugIds: selectedDrugIds,
            })
          } catch (drugErr) {
            const axiosError = drugErr as AxiosError
            errors.push({
              type: 'drugs' as const,
              message:
                (axiosError.response?.data as { detail?: string })?.detail ||
                '医薬品の追加に失敗しました',
            })
          }
        }

        // 4. 結果処理
        if (errors.length > 0) {
          // 部分的な成功
          setPartialErrors(errors)
          setSaveSuccess(true)
          setPartialSuccess(true)

          // トースト通知
          showToast(
            '一部の処理でエラーが発生しましたが、グループは作成されました',
            'warning'
          )

          // アラートは自動的に表示される（JSX部分で条件付きレンダリング）
        } else {
          // 完全成功
          setSaveSuccess(true)

          // トースト通知
          showToast('医薬品グループが正常に作成されました', 'success')

          // 画面遷移 (常に一覧画面に戻る)
          setTimeout(() => {
            navigate('/portal/soaps?tab=groups')
          }, 2000)
        }
      }
    } catch (err) {
      // グループ保存エラー
      if (import.meta.env.DEV) {
        // Dev-only error logging would go here
      }

      const axiosError = err as AxiosError
      const errorMsg =
        axiosError.response?.data?.detail ||
        '医薬品グループの作成に失敗しました'
      setErrorMessage(errorMsg)

      // トースト通知
      showToast(errorMsg, 'error')
    } finally {
      setIsProcessing(false)
    }
  }

  // エラー状態をすべてクリア
  const clearAllErrors = useCallback(() => {
    setNameError('')
    setPartialErrors([])
    setEmptyItemsWarning({
      show: false,
      items: [],
    })
    setErrorMessage('')
  }, [])

  // キャンセル処理
  const handleCancel = () => {
    if (
      groupName ||
      groupDescription ||
      soapTemplates.length > 1 ||
      (soapTemplates[0]?.templates.length > 0 &&
        soapTemplates[0]?.templates.some((t) => t.content))
    ) {
      if (window.confirm('入力内容が破棄されますが、よろしいですか？')) {
        // 警告表示とエラー状態をクリア
        clearAllErrors()
        navigate('/portal/soaps?tab=groups')
      }
    } else {
      // 入力内容がない場合は確認なしで戻る
      // 警告表示とエラー状態をクリア
      clearAllErrors()
      navigate('/portal/soaps?tab=groups')
    }
  }

  // グループ名変更ハンドラ
  const handleGroupNameChange = useCallback((groupId: string, name: string) => {
    setSoapTemplates((prevTemplates) =>
      prevTemplates.map((group) =>
        group.id === groupId ? { ...group, name } : group
      )
    )
  }, [])

  // キーワード変更ハンドラ
  const handleKeywordsChange = useCallback(
    (groupId: string, keywords: string[]) => {
      setSoapTemplates((prevTemplates) =>
        prevTemplates.map((group) =>
          group.id === groupId ? { ...group, keywords } : group
        )
      )
    },
    []
  )

  // SOAP項目追加ハンドラ
  const handleAddItem = useCallback((groupId: string, soapType: SoapType) => {
    setSoapTemplates((prevTemplates) =>
      prevTemplates.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            templates: [
              ...group.templates,
              {
                id: `new-item-${Date.now()}`,
                soap_type: soapType,
                content: '',
              },
            ],
          }
        }
        return group
      })
    )
  }, [])

  // SOAP項目削除ハンドラ
  const handleDeleteItem = useCallback((groupId: string, itemId: string) => {
    setSoapTemplates((prevTemplates) =>
      prevTemplates.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            templates: group.templates.filter((item) => item.id !== itemId),
          }
        }
        return group
      })
    )
  }, [])

  // SOAP項目内容変更ハンドラ
  const handleItemContentChange = useCallback(
    (groupId: string, itemId: string, content: string) => {
      setSoapTemplates((prevTemplates) =>
        prevTemplates.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              templates: group.templates.map((item) =>
                item.id === itemId ? { ...item, content } : item
              ),
            }
          }
          return group
        })
      )
    },
    []
  )

  // グループ削除ハンドラ
  const handleDeleteGroup = useCallback((groupId: string) => {
    setSoapTemplates((prevTemplates) =>
      prevTemplates.filter((group) => group.id !== groupId)
    )
  }, [])

  // タブ切り替えハンドラー
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)

    // 薬剤選択タブに切り替えた際に初回データロード
    if (newValue === 1 && drugs.length === 0 && !searchResults) {
      executeNormalSearch('', 1)
    }
  }

  // エラーがあるかどうか
  const hasError = createGroupMutation.isError || drugError

  return (
    <PortalLayout>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* パンくずリスト */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/portal/soaps"
            onClick={(e) => {
              e.preventDefault()
              navigate('/portal/soaps')
            }}
          >
            SOAPテンプレート管理
          </Link>
          <Typography color="text.primary">新規グループ作成</Typography>
        </Breadcrumbs>

        {/* ヘッダー部分 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            医薬品グループ新規作成
          </Typography>
          <Divider />
        </Box>

        {/* 成功メッセージ（完全成功） */}
        {saveSuccess && !partialSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1">
              医薬品グループが正常に作成されました。
              {selectedDrugIds.length > 0 && '選択した医薬品も追加されました。'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              自動的にSOAPテンプレート一覧画面に戻ります...
            </Typography>
          </Alert>
        )}

        {/* 部分的成功メッセージ */}
        {saveSuccess && partialSuccess && partialErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              医薬品グループは作成されましたが、一部の処理に失敗しました
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {partialErrors.map((error, index) => (
                <Box component="li" key={index}>
                  {error.type === 'soap' ? 'SOAPテンプレート' : '医薬品'}:{' '}
                  {error.message}
                </Box>
              ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              詳細画面で再試行できます。
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/portal/soaps?tab=groups')}
              >
                一覧に戻る
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() =>
                  navigate(`/portal/soaps/groups/${createdGroupId}?edit=true`)
                }
              >
                詳細画面で修正
              </Button>
            </Box>
          </Alert>
        )}

        {/* エラー表示 */}
        {hasError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              保存中にエラーが発生しました
            </Typography>
            <Typography variant="body2">
              {errorMessage || '入力内容を確認して再度お試しください。'}
            </Typography>
          </Alert>
        )}

        {/* 医薬品グループ情報（統合セクション） */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            医薬品グループ情報
          </Typography>
          {/* 基本情報フィールド */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="グループ名"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              margin="normal"
              error={!!nameError}
              helperText={nameError}
              required
            />
            <TextField
              fullWidth
              label="説明"
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>

          {/* タブコンテンツ */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="グループ作成タブ"
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="SOAPテンプレート設定" />
              <Tab
                label={`医薬品選択 ${
                  selectedDrugIds.length > 0
                    ? `(${selectedDrugIds.length})`
                    : ''
                }`}
              />
            </Tabs>

            {/* SOAPテンプレート設定タブ */}
            {activeTab === 0 && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    SOAPテンプレート設定
                  </Typography>
                  <ActionButton
                    variant="contained"
                    size="small"
                    onClick={() => {
                      // 新しいテンプレートグループを追加
                      setSoapTemplates([
                        ...soapTemplates,
                        {
                          id: `new-template-group-${Date.now()}`,
                          name: '新規テンプレート',
                          keywords: [],
                          templates: [
                            {
                              id: `new-item-${Date.now()}`,
                              soap_type: 'S' as SoapType,
                              content: '',
                            },
                          ],
                        },
                      ])
                    }}
                  >
                    + 新規テンプレートグループを追加
                  </ActionButton>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  このグループに関連付けるSOAPテンプレートを設定してください。
                </Typography>

                {soapTemplates.length === 0 ? (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      mb: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      backgroundColor: colorTokens.background.subtle,
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      SOAPテンプレートが存在しません
                    </Typography>
                    <ActionButton
                      variant="contained"
                      onClick={() => {
                        // 新しいテンプレートグループを追加
                        setSoapTemplates([
                          {
                            id: `new-template-group-${Date.now()}`,
                            name: '新規テンプレート',
                            keywords: [],
                            templates: [
                              {
                                id: `new-item-${Date.now()}`,
                                soap_type: 'S' as SoapType,
                                content: '',
                              },
                            ],
                          },
                        ])
                      }}
                    >
                      テンプレートグループを作成
                    </ActionButton>
                  </Paper>
                ) : (
                  soapTemplates.map((group) => (
                    <SoapTemplateSection
                      key={group.id}
                      group={group}
                      editMode={true}
                      onGroupNameChange={handleGroupNameChange}
                      onKeywordsChange={handleKeywordsChange}
                      onAddItem={handleAddItem}
                      onDeleteItem={handleDeleteItem}
                      onItemContentChange={handleItemContentChange}
                      onDeleteGroup={handleDeleteGroup}
                    />
                  ))
                )}
              </Box>
            )}

            {/* 医薬品選択タブ */}
            {activeTab === 1 && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">医薬品選択</Typography>
                  <SelectionCount>
                    選択中: {selectedDrugIds.length}件
                  </SelectionCount>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  このグループに関連付ける医薬品を選択してください。あとからでも追加できます。
                </Typography>
                <DrugTable
                  mode="selection"
                  data={drugs}
                  loading={drugsLoading}
                  searchTerm={normalSearchKeyword}
                  onSearchChange={setNormalSearchKeyword}
                  onSearch={handleSearch}
                  onPageChange={handlePageChange}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  selectedDrugIds={selectedDrugIds}
                  onSelectionChange={(newSelection) => {
                    // 全選択・全解除の場合は専用の関数を使用
                    if (
                      newSelection.length === drugs.length &&
                      newSelection.every((id) =>
                        drugs.some((drug) => drug.id === id)
                      )
                    ) {
                      // 全選択の場合
                      selectAllDrugs(drugs)
                    } else if (newSelection.length === 0) {
                      // 全解除の場合
                      deselectAllDrugs(drugs)
                    } else {
                      // 個別選択の場合 - 現在の選択状態を直接設定
                      setSelectedDrugIds(newSelection)
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* 空SOAP要素警告メッセージ（ボタン直前に表示） */}
        {emptyItemsWarning.show && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              空のSOAP項目があります
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              以下のSOAP項目に内容が入力されていません。
              項目に内容を入力するか、不要な項目は削除してください。
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {emptyItemsWarning.items.map((item, index) => (
                <Box component="li" key={index}>
                  {item.groupName}：{item.soapType}項目
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* アクションボタン */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 4,
          }}
        >
          <CancelButton variant="outlined" onClick={handleCancel}>
            キャンセル
          </CancelButton>
          <ActionButton
            variant="contained"
            onClick={handleSave}
            disabled={isProcessing}
            startIcon={
              isProcessing ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isProcessing
              ? '保存中...'
              : selectedDrugIds.length > 0
              ? `${selectedDrugIds.length}件の医薬品と共に保存`
              : '保存'}
          </ActionButton>
        </Box>
      </Paper>
    </PortalLayout>
  )
}

export default DrugGroupCreatePage
